import React, { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';
import PreloaderScene from './PreloaderScene';
import { PhaserGameScene } from './PhaserGameScene';

// ✅ Extend the global `window` object to properly type AnimatedTiles
declare global {
  interface Window {
    AnimatedTiles: typeof Phaser.Plugins.ScenePlugin;  // ✅ Now properly typed
  }
}

const GameScene: React.FC = () => {
  const gameRef = useRef<HTMLDivElement>(null);
  const gameRefInstance = useRef<Phaser.Game | null>(null);
  const [gameError, setGameError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (gameRef.current) {
      try {
        // ✅ Check if Phaser is available
        if (typeof Phaser === 'undefined') {
          throw new Error("Phaser is not loaded! Check your internet connection or try refreshing the page.");
        }

        // ✅ Check if AnimatedTiles is available
        if (!window.AnimatedTiles) {
          console.warn("AnimatedTiles plugin is missing! Some animations may not work.");
        }

        console.log("Initializing Phaser game...");
        setIsLoading(true);

        const config: Phaser.Types.Core.GameConfig = {
          type: Phaser.AUTO,
          parent: gameRef.current,
          width: 480, // Match tilemap size (30 * 16)
          height: 320, // Match tilemap size (20 * 16)
          backgroundColor: '#2d2d2d',
          scene: [PreloaderScene, PhaserGameScene], // Add PreloaderScene first
          scale: {
            mode: Phaser.Scale.FIT,
            autoCenter: Phaser.Scale.CENTER_BOTH,
          },
          physics: {
            default: 'arcade',
            arcade: {
              gravity: { x: 0, y: 0 },
              debug: false
            }
          },
          // ✅ Register the plugin with proper type
          plugins: {
            scene: [
              {
                key: 'AnimatedTiles',
                plugin: window.AnimatedTiles,  // ✅ No `any` warnings now!
                mapping: 'animatedTiles',
              },
            ],
          },
          // Enable FPS display in dev mode
          fps: {
            min: 30,
            target: 60,
            forceSetTimeOut: true
          },
          render: {
            pixelArt: true,
            antialias: false,
            roundPixels: true
          }
        };

        // Create new game instance
        gameRefInstance.current = new Phaser.Game(config);

        // Game created successfully
        console.log("Phaser game created successfully");

        // Add an event listener to detect when the game is fully ready
        gameRefInstance.current.events.once('ready', () => {
          setIsLoading(false);
          console.log("Phaser game is ready");
        });

        // Handle window resize for responsiveness
        const resizeGame = () => {
          if (gameRefInstance.current) {
            gameRefInstance.current.scale.resize(window.innerWidth, window.innerHeight);
          }
        };
        window.addEventListener('resize', resizeGame);

        return () => {
          console.log("Cleaning up Phaser game");
          gameRefInstance.current?.destroy(true);
          window.removeEventListener('resize', resizeGame);
        };
      } catch (error) {
        console.error("Error initializing game:", error);
        setGameError(error instanceof Error ? error.message : "Failed to initialize game");
        setIsLoading(false);
        return () => {};
      }
    }
  }, []);

  // Show loading or error states
  if (isLoading) {
    return (
      <div ref={gameRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          textAlign: 'center'
        }}>
          <div className="spinner-border text-light mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4>Loading Game...</h4>
        </div>
      </div>
    );
  }

  if (gameError) {
    return (
      <div ref={gameRef} style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Game Error</h4>
          <p>{gameError}</p>
          <hr />
          <p className="mb-0">Please refresh the page or check the console for more details.</p>
        </div>
      </div>
    );
  }

  return <div ref={gameRef} style={{ width: '100%', height: '100%' }} />;
};

export default GameScene;
